import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from '../../i18n/useTranslation';
import { Alert } from '../atoms/Alert/Alert';
import { Badge } from '../atoms/Badge/Badge';
import { Button } from '../atoms/Button/Button';
import { Inline } from '../atoms/Inline/Inline';
import { LiveRegion } from '../atoms/LiveRegion/LiveRegion';
import { PhotoUploadDropzone } from '../atoms/PhotoUploadDropzone/PhotoUploadDropzone';
import { Stack } from '../atoms/Stack/Stack';
import { Heading } from '../atoms/Typography/Heading';
import { Text } from '../atoms/Typography/Text';
import { PhotoUploadQueueList } from '../photoUpload/PhotoUploadQueueList';
import { usePhotoUploadQueue, type PhotoUploadAnnouncement } from '../photoUpload/usePhotoUploadQueue';
import { uploadGalleryImages } from '../../services/galleryService';
import {
  fetchLivestockImageThumbnailUrl,
  linkLivestockImage,
  setMainLivestockImage,
  unlinkLivestockImage,
} from '../../services/livestockService';
import './LivestockPhotosField.css';

export interface LivestockPhotosFieldProps {
  aquariumId: string;
  livestockId: string;
  /** The record's photos as loaded; the first one is the main photo. */
  imageIds: readonly string[];
  /** The animal's name, for the photos' text alternatives. */
  animalName: string;
  disabled?: boolean;
}

type Translate = (key: string, options?: Record<string, unknown>) => string;

function queueAnnouncement(announcement: PhotoUploadAnnouncement | null, t: Translate): string {
  if (announcement === null) return '';
  if (announcement.kind === 'queued') {
    const parts: string[] = [];
    if (announcement.queued > 0) parts.push(t('gallery.uploadQueue.announceQueued', { n: announcement.queued }));
    if (announcement.rejected > 0) parts.push(t('gallery.uploadQueue.announceRejected', { n: announcement.rejected }));
    return parts.join(' ');
  }
  return t('gallery.uploadQueue.announceFinished', { done: announcement.done, failed: announcement.failed });
}

/**
 * The "Photos" field of the livestock edit form: the animal's photos as thumbnails
 * (the first is the main photo, shown in the livestock list) with "set as main" and
 * "remove" (inline confirmation — the form already is a dialog), and a drag & drop
 * zone for new photos. New photos go into the aquarium gallery (the shared per-file
 * upload queue: status, progress, retry) and are linked to the animal. Removing only
 * unlinks a photo; it stays in the gallery.
 *
 * The parent keys this component by the record id, so another animal starts fresh.
 */
export function LivestockPhotosField({
  aquariumId,
  livestockId,
  imageIds,
  animalName,
  disabled = false,
}: LivestockPhotosFieldProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const headingId = useId();
  const [photoIds, setPhotoIds] = useState<string[]>(() => (Array.isArray(imageIds) ? [...imageIds] : []));
  const [urls, setUrls] = useState<Record<string, string | null>>({});
  const [confirmRemoveId, setConfirmRemoveId] = useState<string | null>(null);
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [message, setMessage] = useState('');
  const dropzoneRef = useRef<HTMLDivElement>(null);
  const confirmRef = useRef<HTMLButtonElement>(null);
  const removeRefs = useRef(new Map<string, HTMLButtonElement>());
  const focusAfterRender = useRef<(() => void) | null>(null);
  const requestedRef = useRef(new Set<string>());
  const mountedRef = useRef(true);

  // Thumbnails of photos not requested yet (each image once).
  useEffect(() => {
    const missing = photoIds.filter((id) => !requestedRef.current.has(id));
    if (missing.length === 0) return;
    missing.forEach((id) => requestedRef.current.add(id));
    missing.forEach((id) => {
      void fetchLivestockImageThumbnailUrl(aquariumId, id).then((url) => {
        if (mountedRef.current) setUrls((prev) => ({ ...prev, [id]: url }));
      });
    });
  }, [aquariumId, photoIds]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (confirmRemoveId !== null) confirmRef.current?.focus();
  }, [confirmRemoveId]);

  useEffect(() => {
    const focus = focusAfterRender.current;
    focusAfterRender.current = null;
    focus?.();
  });

  const refreshLists = useCallback(() => {
    // = livestockKeys.all(aquariumId): the list (thumbnail) and the record queries.
    void queryClient.invalidateQueries({ queryKey: ['livestock', aquariumId] });
  }, [aquariumId, queryClient]);

  const upload = useCallback(
    async (file: File, onProgress: (percent: number) => void) => {
      const [image] = await uploadGalleryImages(aquariumId, [file], onProgress);
      if (!image) throw new Error('upload returned no image');
      await linkLivestockImage(aquariumId, livestockId, image.id);
      setPhotoIds((prev) => (prev.includes(image.id) ? prev : [...prev, image.id]));
    },
    [aquariumId, livestockId],
  );

  const onBatchFinished = useCallback(
    ({ done }: { done: number }) => {
      if (done > 0) {
        refreshLists();
        void queryClient.invalidateQueries({ queryKey: ['galleryImages', aquariumId] });
      }
    },
    [aquariumId, queryClient, refreshLists],
  );

  const queue = usePhotoUploadQueue({ upload, onBatchFinished, idPrefix: 'livestock-photo-upload' });

  useEffect(() => {
    const text = queueAnnouncement(queue.announcement, t);
    if (text) setMessage(text);
  }, [queue.announcement, t]);

  const photoLabel = (index: number) => t('livestock.photos.photoNumber', { index: index + 1 });

  const handleSetMain = useCallback(
    async (imageId: string) => {
      setPendingId(imageId);
      setActionError(null);
      try {
        await setMainLivestockImage(aquariumId, livestockId, imageId);
        setPhotoIds((prev) => [imageId, ...prev.filter((id) => id !== imageId)]);
        setMessage(t('livestock.photos.mainSet'));
        refreshLists();
        // The "set as main" button disappears with the move — keep focus on the photo.
        focusAfterRender.current = () => removeRefs.current.get(imageId)?.focus();
      } catch {
        setActionError(t('livestock.photos.mainError'));
      } finally {
        setPendingId(null);
      }
    },
    [aquariumId, livestockId, refreshLists, t],
  );

  const handleRemove = useCallback(
    async (imageId: string) => {
      setPendingId(imageId);
      setActionError(null);
      try {
        await unlinkLivestockImage(aquariumId, livestockId, imageId);
        setPhotoIds((prev) => prev.filter((id) => id !== imageId));
        setConfirmRemoveId(null);
        setMessage(t('livestock.photos.removed'));
        refreshLists();
        focusAfterRender.current = () => dropzoneRef.current?.focus();
      } catch {
        setActionError(t('livestock.photos.removeError'));
      } finally {
        setPendingId(null);
      }
    },
    [aquariumId, livestockId, refreshLists, t],
  );

  const cancelRemove = (imageId: string) => {
    setConfirmRemoveId(null);
    focusAfterRender.current = () => removeRefs.current.get(imageId)?.focus();
  };

  return (
    <section className="livestock-photos" aria-labelledby={headingId} data-testid="livestock-photos">
      <Stack gap="3">
        <Heading level={3} id={headingId} className="livestock-photos__heading">
          {t('livestock.form.photos')}
        </Heading>

        {actionError && (
          <Alert compact onDismiss={() => setActionError(null)} dismissLabel={t('livestock.photos.dismissError')}>
            {actionError}
          </Alert>
        )}

        {photoIds.length === 0 ? (
          <Text color="muted" size="sm">{t('livestock.photos.none')}</Text>
        ) : (
          <ul className="livestock-photos__list" aria-label={t('livestock.photos.listLabel', { name: animalName })}>
            {photoIds.map((imageId, index) => {
              const isMain = index === 0;
              const url = urls[imageId];
              const alt = t('livestock.photos.photoAlt', { index: index + 1, name: animalName });
              const busy = pendingId === imageId;
              return (
                <li key={imageId} className="livestock-photos__item" data-testid="livestock-photo" data-main={isMain}>
                  <div className="livestock-photos__frame">
                    {url ? (
                      <img src={url} alt={alt} className="livestock-photos__img" />
                    ) : (
                      <span className="livestock-photos__placeholder" role="img" aria-label={alt} />
                    )}
                  </div>
                  {isMain && <Badge tone="info">{t('livestock.photos.main')}</Badge>}
                  {confirmRemoveId === imageId ? (
                    <Stack gap="2" className="livestock-photos__confirm">
                      <Text size="sm">{t('livestock.photos.confirmRemove')}</Text>
                      <Inline gap="2" wrap>
                        <Button
                          ref={confirmRef}
                          variant="danger"
                          size="sm"
                          isLoading={busy}
                          onClick={() => void handleRemove(imageId)}
                          aria-label={`${t('livestock.photos.confirmRemoveAction')}: ${photoLabel(index)}`}
                          data-testid="livestock-photo-confirm-remove"
                        >
                          {t('livestock.photos.confirmRemoveAction')}
                        </Button>
                        <Button variant="secondary" size="sm" onClick={() => cancelRemove(imageId)} disabled={busy}>
                          {t('livestock.photos.cancelRemove')}
                        </Button>
                      </Inline>
                    </Stack>
                  ) : (
                    <Inline gap="2" wrap className="livestock-photos__actions">
                      {!isMain && (
                        <Button
                          variant="secondary"
                          size="sm"
                          isLoading={busy}
                          disabled={disabled || (pendingId !== null && !busy)}
                          onClick={() => void handleSetMain(imageId)}
                          aria-label={`${t('livestock.photos.setMain')}: ${photoLabel(index)}`}
                          data-testid="livestock-photo-set-main"
                        >
                          {t('livestock.photos.setMain')}
                        </Button>
                      )}
                      <Button
                        ref={(el: HTMLButtonElement | null) => {
                          if (el) removeRefs.current.set(imageId, el);
                          else removeRefs.current.delete(imageId);
                        }}
                        variant="ghost"
                        size="sm"
                        disabled={disabled || pendingId !== null}
                        onClick={() => {
                          setActionError(null);
                          setConfirmRemoveId(imageId);
                        }}
                        aria-label={`${t('livestock.photos.remove')}: ${photoLabel(index)}`}
                        data-testid="livestock-photo-remove"
                      >
                        {t('livestock.photos.remove')}
                      </Button>
                    </Inline>
                  )}
                </li>
              );
            })}
          </ul>
        )}

        <PhotoUploadDropzone
          ref={dropzoneRef}
          multiple
          onFilesChange={queue.addFiles}
          disabled={disabled}
        />

        <PhotoUploadQueueList
          items={queue.items}
          busy={queue.busy}
          onRetry={queue.retry}
          onClear={queue.clearFinished}
          titleLevel={4}
          testIdPrefix="livestock-photo-upload"
        />

        <LiveRegion visuallyHidden aria-atomic="true" data-testid="livestock-photos-announcer">
          {message}
        </LiveRegion>
      </Stack>
    </section>
  );
}

export default LivestockPhotosField;
